"use strict";
/**
 * Shared QR code styling vocabulary and options builder.
 *
 * Both the web preview and the API's download renderer build their
 * `qr-code-styling` options from this one function, so what a customer sees in
 * the editor is what they get in the downloaded file. Previously each side
 * hardcoded its own options object and the two had already drifted: the web
 * preview set `errorCorrectionLevel` to 'H' with a logo and 'L' without, while
 * the API set it not at all — which left the library on its 'Q' default. So a
 * logo'd code downloaded weaker than it previewed, and a plain one downloaded
 * stronger. Neither side was deliberate.
 *
 * This module deliberately has no dependency on `qr-code-styling` itself — it
 * only produces a plain options object — so it stays importable from the API,
 * the web app, and anything else that needs to describe a QR code.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getContrastWarning = exports.contrastRatio = exports.MIN_SCANNABLE_CONTRAST_RATIO = exports.matchQRStylePreset = exports.qrStyleFingerprint = exports.qrStylesEqual = exports.QR_STYLE_PRESETS = exports.resolveQuietModules = exports.CIRCULAR_QUIET_MODULES = exports.buildQRCodeOptions = exports.resolveFinderTypes = exports.sanitizeQRStyleConfigForWrite = exports.InvalidQRStyleError = exports.sanitizeQRStyleConfig = exports.parseQRStyleConfig = exports.MAX_CIRCULAR_QUIET_MODULES = exports.MIN_CIRCULAR_QUIET_MODULES = exports.MAX_GRADIENT_COLOR_STOPS = exports.QR_CODE_SHAPES = exports.QR_CORNER_DOT_TYPES = exports.QR_CORNER_SQUARE_TYPES = exports.QR_DOT_TYPES = exports.styleForDownloadBackground = exports.TRANSPARENT_BACKGROUND = exports.LOGO_MARGIN_AT_DEFAULT_SIZE = exports.DEFAULT_QR_SIZE = exports.DEFAULT_QR_STYLE = void 0;
/**
 * What a code with no saved style looks like — the product's own default, not
 * the library's.
 *
 * This is deliberately the brand default rather than a plain black square. It
 * has to live HERE, in the one module both renderers read, because "the default
 * appearance" is a value each side would otherwise invent for itself: the app
 * previously carried its own default (classy dots in `#143444`) in
 * `packages/app/src/lib/qr-style.ts` and applied it whenever `qr_style` was
 * null, while the download path fell through to `style = {}` and rendered plain
 * black. Every code that had never been styled therefore previewed navy and
 * downloaded black — the single widest preview-vs-file mismatch there was, and
 * one nobody would notice until a file was opened.
 *
 * Note what is NOT here: `cornersSquareType`. `resolveFinderTypes` already
 * derives `extra-rounded` from a `classy` dots type, so stating it would be
 * redundant — and stating it would ALSO become a blanket fallback for styles
 * that only set `dotsType`, which would silently re-round the finder patterns of
 * the explicitly-square `Classic` preset. The default is expressed as the
 * minimum set of fields that produces the intended picture.
 */
exports.DEFAULT_QR_STYLE = {
    dotsType: 'classy',
    dotsColor: '#143444',
    backgroundColor: '#ffffff',
};
/** Rendered size in px when a caller doesn't specify one. */
exports.DEFAULT_QR_SIZE = 500;
/**
 * Clear space around the centre logo, in px **at `DEFAULT_QR_SIZE`**.
 *
 * `qr-code-styling` takes `imageOptions.margin` in absolute pixels, which makes
 * it the one part of a style that does not scale with the render. Every other
 * knob is proportional — `imageSize` is a coefficient, dot geometry derives from
 * the module size — so a fixed margin quietly made the same code a different
 * picture at every size we render at. Measured, same code and logo:
 *
 *   size   logo width as a fraction of the canvas
 *    160   22.5%   (grid thumbnail)
 *    500   33.6%   (detail preview, and the legacy app's download)
 *   2048   36.6%   (this app's download)
 *
 * Visible as a thumbnail whose logo is obviously smaller than the one on the
 * code's own screen, and a download that did not quite match either. So the
 * margin is scaled from this anchor in `buildQRCodeOptions` and the rendered
 * result is size-invariant.
 *
 * The anchor is `DEFAULT_QR_SIZE` rather than any particular download size,
 * because download size is the caller's choice (this app asks for 2048, the
 * legacy one asked for 500) and an anchor that moves with it would not be an
 * anchor. 500 also keeps the appearance that most existing printed artwork was
 * generated at.
 */
exports.LOGO_MARGIN_AT_DEFAULT_SIZE = 10;
/** Sentinel `backgroundColor` meaning "no background rect at all". */
exports.TRANSPARENT_BACKGROUND = 'transparent';
/**
 * The style to render a DOWNLOAD with, given the caller's background choice.
 *
 * Kept here rather than in the download handler because it is the same decision
 * both renderers already implement in terms of `backgroundColor` — expressing
 * the choice as a style override means the square path, the circular path and
 * every file format inherit it without any of them learning a new concept.
 *
 * `undefined` returns the saved style untouched (and, importantly, the same
 * object identity semantics: a null style stays null rather than becoming `{}`).
 */
function styleForDownloadBackground(style, transparentBackground) {
    if (transparentBackground === undefined)
        return style;
    if (transparentBackground) {
        return Object.assign(Object.assign({}, style), { backgroundColor: exports.TRANSPARENT_BACKGROUND });
    }
    const saved = style === null || style === void 0 ? void 0 : style.backgroundColor;
    return Object.assign(Object.assign({}, style), { backgroundColor: saved && saved !== exports.TRANSPARENT_BACKGROUND
            ? saved
            : exports.DEFAULT_QR_STYLE.backgroundColor });
}
exports.styleForDownloadBackground = styleForDownloadBackground;
exports.QR_DOT_TYPES = [
    'square',
    'dots',
    'rounded',
    'extra-rounded',
    'classy',
    'classy-rounded',
];
exports.QR_CORNER_SQUARE_TYPES = [
    'square',
    'dot',
    'extra-rounded',
];
exports.QR_CORNER_DOT_TYPES = [
    'square',
    'dot',
];
exports.QR_CODE_SHAPES = ['square', 'circular'];
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
function pickEnum(value, allowed) {
    return typeof value === 'string' && allowed.includes(value)
        ? value
        : undefined;
}
function pickColor(value) {
    return typeof value === 'string' && HEX_COLOR.test(value)
        ? value.toLowerCase()
        : undefined;
}
/**
 * Backgrounds additionally accept `transparent`. Downloads used to have their
 * background rect stripped by the old browser post-processing step, so keeping
 * transparency available preserves what designers could previously place over
 * their own artwork.
 */
function pickBackgroundColor(value) {
    if (value === exports.TRANSPARENT_BACKGROUND)
        return exports.TRANSPARENT_BACKGROUND;
    return pickColor(value);
}
/**
 * Upper bound on gradient stops. Far beyond any real gradient — the point is
 * that this is the only gate on the value: an unbounded list is accepted,
 * persisted into jsonb, and then rebuilt on every preview render and every
 * download, so one crafted request would permanently bloat the row.
 */
exports.MAX_GRADIENT_COLOR_STOPS = 10;
/**
 * Bounds on a customer-supplied quiet zone.
 *
 * The floor is 1, not 0. With filler butted directly against the code, denser
 * codes stop decoding — a short payload at a generous output size survives it,
 * so "never decodes" would be too strong, but the failure arrives well within
 * the range customers actually use. Zero is a broken code rather than a style
 * choice.
 *
 * The ceiling is a sanity bound rather than a file-size control. At 8 the
 * supergrid is already more than twice the code's width, but payload length
 * dominates output size by an order of magnitude — a max-version code is near
 * 4 MB of SVG whatever the quiet zone, which moves it by roughly 6%.
 */
exports.MIN_CIRCULAR_QUIET_MODULES = 1;
exports.MAX_CIRCULAR_QUIET_MODULES = 8;
/**
 * The circular quiet zone, as whole modules within the supported range.
 *
 * An out-of-range value is **dropped**, not clamped and not an error — the same
 * treatment every other field in this sanitizer gets. The stored style then
 * carries no quiet zone and the renderer falls back to the default, so a client
 * sending 99 gets a code at the default rather than a rejected request.
 *
 * A request whose *only* recognizable field is a bad quiet zone still fails,
 * because nothing survives sanitization at all — see
 * `sanitizeQRStyleConfigForWrite`. That asymmetry is inherent to sanitizing
 * field-by-field and is not specific to this field.
 *
 * `resolveQuietModules` clamps rather than drops, so a value stored before
 * these bounds existed still renders something sane.
 *
 * The bounds are declared above this function deliberately: it only reads them
 * inside a call today, but a module-level read would hit the temporal dead
 * zone.
 */
function pickQuietModules(value) {
    if (typeof value !== 'number' || !Number.isInteger(value))
        return undefined;
    if (value < exports.MIN_CIRCULAR_QUIET_MODULES ||
        value > exports.MAX_CIRCULAR_QUIET_MODULES) {
        return undefined;
    }
    return value;
}
function pickGradient(value) {
    if (!value || typeof value !== 'object')
        return undefined;
    const candidate = value;
    const type = pickEnum(candidate.type, ['linear', 'radial']);
    if (!type)
        return undefined;
    if (!Array.isArray(candidate.colorStops))
        return undefined;
    if (candidate.colorStops.length > exports.MAX_GRADIENT_COLOR_STOPS)
        return undefined;
    const colorStops = candidate.colorStops
        .map((stop) => {
        const entry = stop;
        const color = pickColor(entry === null || entry === void 0 ? void 0 : entry.color);
        const offset = typeof (entry === null || entry === void 0 ? void 0 : entry.offset) === 'number' ? entry.offset : undefined;
        if (color === undefined || offset === undefined)
            return undefined;
        return { offset: Math.min(1, Math.max(0, offset)), color };
    })
        .filter((stop) => !!stop);
    // A gradient needs at least two stops to mean anything.
    if (colorStops.length < 2)
        return undefined;
    /* `typeof === 'number'` admits Infinity and NaN — and `JSON.parse('1e999')`
       produces exactly Infinity from a plain request body. Stored, it violates
       the declared `rotation?: number` and collapses the gradient to zero
       length at render time. */
    const rotation = Number.isFinite(candidate.rotation)
        ? candidate.rotation
        : undefined;
    return Object.assign({ type, colorStops }, (rotation !== undefined ? { rotation } : {}));
}
/**
 * Normalize a `qrStyle` as it arrives from a read, into a real object.
 *
 * The column is jsonb, and the two readers disagree about what that means.
 * TypeORM parses it, so the API sees an object. pg_graphql serializes it to a
 * `JSON` scalar, which reaches the browser as a *string* of JSON — so the web
 * app was handing the renderer `'{"shape":"circular",...}'`. Property access
 * on a string yields undefined for every option, and every preview quietly
 * drew the default black square code while the download (server-rendered, via
 * TypeORM) drew the right one.
 *
 * Codegen types the `JSON` scalar as `any`, so the cast at the call sites type
 * checked and hid this. Accept both shapes rather than assuming one: the
 * transport decides, and a local Supabase on a different pg_graphql version
 * may decide differently.
 */
function parseQRStyleConfig(input) {
    if (input === null || input === undefined)
        return null;
    let candidate = input;
    /* Decoded exactly once, deliberately. Re-parsing until it stops being a
       string would also accept a double-encoded value, which is a bug at
       whatever wrote it rather than something to paper over — and it would
       recurse once per layer on input we do not control. */
    if (typeof candidate === 'string') {
        try {
            candidate = JSON.parse(candidate);
        }
        catch (_a) {
            /* Stored jsonb is always valid JSON, so this means something other than
               a style reached us. Render the default rather than throw inside a
               preview. */
            return null;
        }
    }
    /* Arrays and primitives are not style objects — `null` renders the default.
       `typeof null` is 'object', so the null check has to survive the parse. */
    if (typeof candidate !== 'object' ||
        candidate === null ||
        Array.isArray(candidate)) {
        return null;
    }
    return candidate;
}
exports.parseQRStyleConfig = parseQRStyleConfig;
/**
 * Allowlist-sanitize an untrusted style config before persisting it.
 *
 * The API's ValidationPipe runs without `whitelist` or `transform`, so unknown
 * properties on a request body pass straight through and nested class
 * validation never fires. Since this value is stored as jsonb and later fed to
 * the renderer, sanitize rather than validate: unrecognized keys and malformed
 * values are dropped instead of persisted.
 *
 * Returns null when nothing usable survives, which renders the default code.
 */
function sanitizeQRStyleConfig(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input))
        return null;
    const raw = input;
    const sanitized = {
        dotsType: pickEnum(raw.dotsType, exports.QR_DOT_TYPES),
        dotsColor: pickColor(raw.dotsColor),
        dotsGradient: pickGradient(raw.dotsGradient),
        cornersSquareType: pickEnum(raw.cornersSquareType, exports.QR_CORNER_SQUARE_TYPES),
        cornersSquareColor: pickColor(raw.cornersSquareColor),
        cornersDotType: pickEnum(raw.cornersDotType, exports.QR_CORNER_DOT_TYPES),
        cornersDotColor: pickColor(raw.cornersDotColor),
        backgroundColor: pickBackgroundColor(raw.backgroundColor),
        shape: pickEnum(raw.shape, exports.QR_CODE_SHAPES),
        circularOutline: typeof raw.circularOutline === 'boolean' ? raw.circularOutline : undefined,
        circularOutlineColor: pickColor(raw.circularOutlineColor),
        circularQuietModules: pickQuietModules(raw.circularQuietModules),
    };
    // Drop undefined keys so we store a tight object rather than a bag of nulls.
    const entries = Object.entries(sanitized).filter(([, value]) => value !== undefined);
    return entries.length ? Object.fromEntries(entries) : null;
}
exports.sanitizeQRStyleConfig = sanitizeQRStyleConfig;
/** Thrown when a caller sends a style object of which nothing is recognizable. */
class InvalidQRStyleError extends Error {
    constructor() {
        super('The supplied QR style contained no recognizable settings. Colours must be 6-digit hex, and shapes must be one of the supported values.');
        this.name = 'InvalidQRStyleError';
    }
}
exports.InvalidQRStyleError = InvalidQRStyleError;
/**
 * Sanitize a style for persistence, distinguishing "reset me" from "I sent
 * something you did not understand".
 *
 * `sanitizeQRStyleConfig` returns null for both an explicit null and an object
 * whose every key failed the allowlist. Writing that null straight through
 * means a request carrying e.g. 3-digit hex or a renamed dot type silently
 * resets the customer's saved style and still responds 200 — indistinguishable
 * from the documented "explicit null resets to default" path. Callers get no
 * signal that anything was dropped.
 *
 * Returns null only when the caller genuinely asked for a reset; throws
 * otherwise so the route can surface a 400.
 */
function sanitizeQRStyleConfigForWrite(input) {
    if (input === null || input === undefined)
        return null;
    const sanitized = sanitizeQRStyleConfig(input);
    if (!sanitized)
        throw new InvalidQRStyleError();
    return sanitized;
}
exports.sanitizeQRStyleConfigForWrite = sanitizeQRStyleConfigForWrite;
/**
 * Dot styles that leave modules visually disconnected. When the three finder
 * patterns (the big corner squares a scanner uses to locate the code) inherit
 * one of these, they render as rings of loose circles and the code becomes
 * undecodable — verified against both jsQR and zxing, at every error
 * correction level.
 *
 * The data modules themselves are fine in these styles; only the finder
 * patterns must stay solid.
 */
const FINDER_UNSAFE_DOT_TYPES = ['dots'];
/**
 * `qr-code-styling` falls back to `dotsOptions.type` for the finder patterns
 * when corner options are omitted. That fallback is what produces unscannable
 * codes, so we always resolve the corner types explicitly rather than letting
 * the library inherit.
 *
 * Exported because the customizer has the same question: a style that has not
 * set `cornersSquareType` still renders SOME finder shape, and the control has
 * to highlight the one actually in effect. Re-deriving that rule in the UI is
 * how a form ends up disagreeing with the picture beside it.
 */
function resolveFinderTypes(style) {
    var _a, _b, _c;
    const dotsType = (_a = style.dotsType) !== null && _a !== void 0 ? _a : exports.DEFAULT_QR_STYLE.dotsType;
    const inheritIsUnsafe = FINDER_UNSAFE_DOT_TYPES.includes(dotsType);
    const cornersSquareType = (_b = style.cornersSquareType) !== null && _b !== void 0 ? _b : (inheritIsUnsafe
        ? 'square'
        : // These are the dot types that are also valid corner-square types.
            dotsType === 'extra-rounded' || dotsType === 'square'
                ? dotsType
                : 'extra-rounded');
    // `dot` inner corners were unreadable to jsQR at error correction L, though
    // fine from M upward. We now use Q or H, so this is a conservative default
    // rather than a hard requirement; the customizer can still set `dot`.
    const cornersDotType = (_c = style.cornersDotType) !== null && _c !== void 0 ? _c : 'square';
    return { cornersSquareType, cornersDotType };
}
exports.resolveFinderTypes = resolveFinderTypes;
/**
 * Build the `qr-code-styling` options object. Pure — safe to call from a React
 * render path or a request handler.
 */
function buildQRCodeOptions(args) {
    var _a, _b, _c, _d, _e;
    const { data, image } = args;
    const size = (_a = args.size) !== null && _a !== void 0 ? _a : exports.DEFAULT_QR_SIZE;
    const style = (_b = args.style) !== null && _b !== void 0 ? _b : {};
    const { cornersSquareType, cornersDotType } = resolveFinderTypes(style);
    const dotsColor = (_c = style.dotsColor) !== null && _c !== void 0 ? _c : exports.DEFAULT_QR_STYLE.dotsColor;
    /**
     * Finder patterns inherit the dots' paint when the customizer hasn't set a
     * dedicated colour. Falling back to a flat colour here would paint them the
     * default black even on a gradient style with a dark background, leaving the
     * three markers a scanner needs invisible.
     */
    const finderPaint = (explicitColor) => {
        if (explicitColor)
            return { color: explicitColor };
        if (style.dotsGradient)
            return { gradient: style.dotsGradient };
        return { color: dotsColor };
    };
    return Object.assign(Object.assign({ width: size, height: size, type: 'svg', data }, (image ? { image } : {})), { qrOptions: {
            /**
             * A center logo occludes modules, so it needs the highest error
             * correction to stay scannable. Otherwise use Q — codes get printed on
             * stickers and decals, where scratches and glare eat into the margin,
             * and Q is also what the download path produced before these options
             * were shared (omitting `qrOptions` left the library on its 'Q' default).
             */
            errorCorrectionLevel: (image ? 'H' : 'Q'),
        }, imageOptions: {
            crossOrigin: 'anonymous',
            imageSize: 0.5,
            /* Scaled, so the logo occupies the same fraction of the code whatever
               size it is drawn at — see `LOGO_MARGIN_AT_DEFAULT_SIZE`. A thumbnail, a
               preview and the downloaded file are meant to be one picture at three
               resolutions, and an absolute margin here was the last thing stopping
               them from being exactly that. */
            margin: (size / exports.DEFAULT_QR_SIZE) * exports.LOGO_MARGIN_AT_DEFAULT_SIZE,
        }, dotsOptions: Object.assign({ type: (_d = style.dotsType) !== null && _d !== void 0 ? _d : exports.DEFAULT_QR_STYLE.dotsType }, (style.dotsGradient
            ? { gradient: style.dotsGradient }
            : { color: dotsColor })), cornersSquareOptions: Object.assign({ type: cornersSquareType }, finderPaint(style.cornersSquareColor)), cornersDotOptions: Object.assign({ type: cornersDotType }, finderPaint(style.cornersDotColor)), backgroundOptions: {
            color: (_e = style.backgroundColor) !== null && _e !== void 0 ? _e : exports.DEFAULT_QR_STYLE.backgroundColor,
        } });
}
exports.buildQRCodeOptions = buildQRCodeOptions;
/**
 * Clear modules between the real code and the filler, on every side.
 *
 * Four, as ISO/IEC 18004 asks for. The supergrid is sized from this value (see
 * `supergridSize`), so it can be changed freely — the grid grows or shrinks
 * with it and the circular style survives either way.
 *
 * **This is spec compliance bought at a measured cost, not a scannability
 * win.** Because the grid is fitted into a fixed canvas, extra quiet zone is
 * paid for out of module resolution rather than being margin around the symbol
 * as it is on a normal QR. Blur tolerance, as the largest Gaussian sigma at
 * which both jsQR and zxing still decode a downscaled render:
 *
 *   capture   quiet=2   quiet=4
 *     220px     2.38      1.98
 *     180px     2.03      1.60
 *     150px     1.63      1.10
 *     120px     1.35      1.10
 *
 * Worse or equal at every capture width. Rendering to a larger canvas does not
 * recover it — what matters is modules per captured pixel, and a bigger source
 * image downscales to the same thing (measured: 1.93 at 220px from a canvas
 * scaled to hold module size constant, against 1.98 unscaled).
 *
 * Treat those figures as indicative rather than exact: they come from a
 * synthetic downscale-and-blur model, so they move with the sigma step, the
 * early-stop rule and the payload set. A second independent harness produced
 * the same ordering and mechanism with absolute values ~10-20% lower. Neither
 * is a camera — real phone decoders are more tolerant than jsQR and zxing — so
 * a physical print at the smallest size you recommend is what would settle it,
 * and is the natural trigger for the revert path below.
 *
 * So the trade is: a wider quiet zone helps a decoder *locate* the symbol
 * against surrounding content, while module resolution governs whether it can
 * *resolve* the modules at all. Here the surrounding content is our own filler,
 * which we control.
 *
 * This is the default. It can be overridden per code via
 * `QRStyleConfig.circularQuietModules`, so the trade-off can be made per use
 * rather than globally — a code destined for a small print can drop to 2 while
 * the default stays spec-conformant.
 */
exports.CIRCULAR_QUIET_MODULES = 4;
/** The quiet zone a style asks for, falling back to the default. */
function resolveQuietModules(style) {
    const requested = style === null || style === void 0 ? void 0 : style.circularQuietModules;
    if (typeof requested !== 'number' || !Number.isInteger(requested)) {
        return exports.CIRCULAR_QUIET_MODULES;
    }
    return Math.min(exports.MAX_CIRCULAR_QUIET_MODULES, Math.max(exports.MIN_CIRCULAR_QUIET_MODULES, requested));
}
exports.resolveQuietModules = resolveQuietModules;
/**
 * Named starting points offered by the customizer.
 *
 * Most customers want their code to look designed rather than to make a series
 * of independent styling decisions — and "can we have a circular one?" is
 * answered by a single click here.
 *
 * Defined in the SDK rather than the web app so the render verification can
 * prove every shipped preset actually scans.
 */
exports.QR_STYLE_PRESETS = [
    {
        id: 'classic',
        label: 'Classic',
        style: {
            dotsType: 'square',
            dotsColor: '#000000',
            backgroundColor: '#ffffff',
        },
    },
    {
        id: 'rounded',
        label: 'Rounded',
        style: {
            dotsType: 'rounded',
            dotsColor: '#000000',
            backgroundColor: '#ffffff',
        },
    },
    {
        id: 'circular',
        label: 'Circular',
        /* Corner types are pinned rather than left to inherit: finder patterns
           drawn as loose circles make the code unreadable. buildQRCodeOptions
           enforces this too, but setting it here keeps the saved config honest. */
        style: {
            dotsType: 'dots',
            dotsColor: '#000000',
            backgroundColor: '#ffffff',
            cornersSquareType: 'extra-rounded',
            cornersDotType: 'dot',
        },
    },
    {
        id: 'circle',
        label: 'Circle',
        style: {
            shape: 'circular',
            dotsColor: '#000000',
            backgroundColor: '#ffffff',
        },
    },
    {
        id: 'circle-outlined',
        label: 'Circle ring',
        style: {
            shape: 'circular',
            dotsColor: '#000000',
            backgroundColor: '#ffffff',
            circularOutline: true,
        },
    },
    {
        id: 'bold',
        label: 'Bold',
        style: {
            dotsType: 'extra-rounded',
            dotsColor: '#000000',
            backgroundColor: '#ffffff',
            cornersSquareType: 'extra-rounded',
        },
    },
];
/**
 * Normalize a style down to what actually affects the rendered code, so two
 * configs that render identically compare equal.
 *
 * Deliberately generic: it starts from a spread of the whole object rather than
 * enumerating known keys. The previous version listed the keys it cared about,
 * which meant adding new keys silently broke preset matching —
 * the Badge preset resolved identically to Classic and highlighted the wrong
 * button. Anything added to QRStyleConfig from now on is included by default.
 */
function normalizeStyleForComparison(style) {
    var _a, _b, _c, _d, _e;
    const resolved = Object.assign({}, style);
    resolved.dotsType = (_a = style.dotsType) !== null && _a !== void 0 ? _a : exports.DEFAULT_QR_STYLE.dotsType;
    resolved.backgroundColor =
        (_b = style.backgroundColor) !== null && _b !== void 0 ? _b : exports.DEFAULT_QR_STYLE.backgroundColor;
    /* A gradient supersedes the flat colour, so a style carrying both renders the
       same as one carrying only the gradient. */
    if (style.dotsGradient) {
        delete resolved.dotsColor;
        resolved.dotsGradient = JSON.stringify(style.dotsGradient);
    }
    else {
        resolved.dotsColor = (_c = style.dotsColor) !== null && _c !== void 0 ? _c : exports.DEFAULT_QR_STYLE.dotsColor;
        delete resolved.dotsGradient;
    }
    /* Outline settings are inert on a square code, and a circular code is drawn
       by its own renderer which ignores the qr-code-styling module options. */
    resolved.shape = (_d = style.shape) !== null && _d !== void 0 ? _d : 'square';
    if (resolved.shape === 'square') {
        delete resolved.circularOutline;
        delete resolved.circularOutlineColor;
        delete resolved.circularQuietModules;
    }
    else {
        resolved.circularQuietModules = resolveQuietModules(style);
        resolved.circularOutline = (_e = style.circularOutline) !== null && _e !== void 0 ? _e : false;
        delete resolved.dotsType;
        delete resolved.dotsGradient;
        delete resolved.cornersSquareType;
        delete resolved.cornersSquareColor;
        delete resolved.cornersDotType;
        delete resolved.cornersDotColor;
    }
    for (const key of Object.keys(resolved)) {
        if (resolved[key] === undefined)
            delete resolved[key];
    }
    /* Stable ordering so key insertion order can't affect the comparison. */
    return Object.fromEntries(Object.keys(resolved)
        .sort()
        .map((key) => [key, resolved[key]]));
}
/**
 * Would these two styles render the same code?
 *
 * Compares the *resolved* forms, so a style that states a default and one that
 * omits it are equal, key order does not matter, and settings the renderer
 * ignores for the chosen shape are disregarded.
 *
 * This is the right question for "which preset is active?" and the wrong one
 * for "should this be saved?" — see `qrStyleFingerprint`.
 */
function qrStylesEqual(a, b) {
    const normalize = (style) => JSON.stringify(normalizeStyleForComparison(style !== null && style !== void 0 ? style : {}));
    return normalize(a) === normalize(b);
}
exports.qrStylesEqual = qrStylesEqual;
/**
 * A stable string identifying a style by the values it actually carries.
 *
 * Backs "has the customer changed the style?" on save. That has to compare the
 * raw values, not the resolved ones: render-equivalence is a strictly coarser
 * relation, and using it silently discards edits the customer made and can see
 * in the form. Measured against `qrStylesEqual`, all of these compare equal
 * while being genuinely different values:
 *
 * - choosing the Classic preset on a code with no stored style — renders the
 *   same, but nothing is written, so no preset highlights on reopen
 * - changing `dotsColor` on a style carrying a gradient — the gradient wins at
 *   render time, so the colour edit is dropped
 * - setting circular-only options while the shape is square — inert now, but
 *   discarded rather than kept for when the shape changes
 *
 * Only key order and absent-vs-undefined are normalized away, since neither is
 * a difference the customer can express. Array order is preserved: gradient
 * stop order is meaningful.
 */
function qrStyleFingerprint(style) {
    const stable = (value) => {
        if (Array.isArray(value))
            return value.map(stable);
        if (value && typeof value === 'object') {
            const source = value;
            return Object.keys(source)
                .filter((key) => source[key] !== undefined)
                .sort()
                .reduce((acc, key) => {
                acc[key] = stable(source[key]);
                return acc;
            }, {});
        }
        return value;
    };
    return JSON.stringify(stable(style !== null && style !== void 0 ? style : {}));
}
exports.qrStyleFingerprint = qrStyleFingerprint;
/**
 * Identify which preset a style matches, for highlighting the active one in the
 * customizer. Returns null once the style has been customized away from all of
 * them.
 *
 * Lives here rather than in the web app so it can be tested — web's CRA jest
 * setup is broken by a hoisted babel version conflict, which is why the preset
 * regression above went unnoticed.
 */
function matchQRStylePreset(style) {
    var _a, _b;
    if (!style)
        return null;
    /* Uses the shared equality rather than repeating it — two copies of "are
       these the same style?" would eventually disagree, and the customizer
       highlighting one preset while the save gate believes another is exactly
       the kind of drift that is hard to notice. */
    return ((_b = (_a = exports.QR_STYLE_PRESETS.find((preset) => qrStylesEqual(style, preset.style))) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null);
}
exports.matchQRStylePreset = matchQRStylePreset;
/**
 * Scanners find a code by thresholding the image into light and dark, so what
 * matters is the contrast between the modules and the background. Below roughly
 * 3:1 that separation gets unreliable on a phone camera, in print, or in poor
 * light — long before it looks wrong on a designer's monitor.
 */
exports.MIN_SCANNABLE_CONTRAST_RATIO = 3;
function parseHex(hex) {
    const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
    if (!match)
        return null;
    const value = parseInt(match[1], 16);
    return {
        r: (value >> 16) & 0xff,
        g: (value >> 8) & 0xff,
        b: value & 0xff,
    };
}
/** WCAG relative luminance. */
function relativeLuminance({ r, g, b, }) {
    const channel = (raw) => {
        const c = raw / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
function contrastRatio(foreground, background) {
    const fg = parseHex(foreground);
    const bg = parseHex(background);
    if (!fg || !bg)
        return null;
    const lighter = Math.max(relativeLuminance(fg), relativeLuminance(bg));
    const darker = Math.min(relativeLuminance(fg), relativeLuminance(bg));
    return (lighter + 0.05) / (darker + 0.05);
}
exports.contrastRatio = contrastRatio;
/**
 * The effective foregrounds for contrast purposes. A gradient is judged by its
 * weakest stop, since the code has to scan across its whole area.
 */
function effectiveForegrounds(style) {
    var _a, _b, _c, _d;
    /**
     * A circular code is drawn by its own renderer, which paints every module —
     * real and filler — in `dotsColor` and ignores gradients and corner colours
     * entirely. Scoring those would warn "won't scan" over values that have no
     * effect on the rendered code.
     */
    if (style.shape === 'circular') {
        return [(_a = style.dotsColor) !== null && _a !== void 0 ? _a : exports.DEFAULT_QR_STYLE.dotsColor];
    }
    const dots = ((_c = (_b = style.dotsGradient) === null || _b === void 0 ? void 0 : _b.colorStops) === null || _c === void 0 ? void 0 : _c.length)
        ? style.dotsGradient.colorStops.map((stop) => stop.color)
        : [(_d = style.dotsColor) !== null && _d !== void 0 ? _d : exports.DEFAULT_QR_STYLE.dotsColor];
    /**
     * The finder patterns matter at least as much as the data modules — they are
     * what a scanner uses to locate the code at all — so score any dedicated
     * corner colours too. Corners without an explicit colour inherit the dots'
     * paint, which is already covered above.
     */
    const corners = [style.cornersSquareColor, style.cornersDotColor].filter((color) => !!color);
    return [...dots, ...corners];
}
/**
 * Returns a warning when the chosen colors are likely to scan poorly, or null
 * when they're fine. Advisory — saving is not blocked, since print and screen
 * conditions vary and the customer may know their use case better than we do.
 */
function getContrastWarning(style) {
    var _a;
    if (!style)
        return null;
    const background = (_a = style.backgroundColor) !== null && _a !== void 0 ? _a : exports.DEFAULT_QR_STYLE.backgroundColor;
    /**
     * A transparent background sits on whatever artwork the customer places it
     * over, which we can't know, so there is nothing meaningful to score.
     */
    if (background === exports.TRANSPARENT_BACKGROUND)
        return null;
    const ratios = effectiveForegrounds(style)
        .map((foreground) => contrastRatio(foreground, background))
        .filter((ratio) => ratio !== null);
    if (!ratios.length)
        return null;
    const worst = Math.min(...ratios);
    if (worst >= exports.MIN_SCANNABLE_CONTRAST_RATIO)
        return null;
    return worst < 1.5
        ? "These colors are too similar — this code most likely won't scan."
        : 'These colors are low contrast, which can make the code hard to scan in print or low light.';
}
exports.getContrastWarning = getContrastWarning;
